import type { CurrentUser } from './generated/types';
import { ActiveApiError, type ActiveTransport } from './transport';

const ROLE_VIEW_SELECTION_KEY = 'quotation-system:role-view:v1';
export type RoleViewOption = {
  id: string;
  key: string;
  name: string;
  authorizationSource?: 'legacy' | 'model3';
};

function owner(user: CurrentUser): string {
  return `${user.companyId}:${user.id}`;
}

export function persistRoleViewSelection(
  user: CurrentUser,
  roleId: string | null,
) {
  try {
    if (roleId && user.roleViewEnabled === true)
      sessionStorage.setItem(
        ROLE_VIEW_SELECTION_KEY,
        JSON.stringify({ owner: owner(user), roleId }),
      );
    else sessionStorage.removeItem(ROLE_VIEW_SELECTION_KEY);
  } catch {
    // Selection remains in the active session; no credential or draft is stored here.
  }
}

/** Restore a selection only after the original server session proves administrator capability. */
export async function restoreRoleViewSelection(
  transport: ActiveTransport,
  original: CurrentUser,
): Promise<CurrentUser> {
  let selection: { owner?: unknown; roleId?: unknown } | null = null;
  try {
    selection = JSON.parse(
      sessionStorage.getItem(ROLE_VIEW_SELECTION_KEY) ?? 'null',
    );
  } catch {
    selection = null;
  }
  if (
    original.roleViewEnabled !== true ||
    selection?.owner !== owner(original) ||
    typeof selection.roleId !== 'string'
  ) {
    transport.roleViewDispatch.select(null);
    persistRoleViewSelection(original, null);
    return original;
  }
  try {
    const viewed = await transport.getCurrentUser(selection.roleId);
    validateRoleView(original, viewed, selection.roleId);
    transport.roleViewDispatch.select(selection.roleId);
    return viewed;
  } catch (error) {
    if (
      !(error instanceof ActiveApiError) ||
      ![400, 403, 404].includes(error.status ?? 0)
    )
      throw error;
    transport.roleViewDispatch.select(null);
    persistRoleViewSelection(original, null);
    return original;
  }
}

export function validateRoleView(
  before: CurrentUser,
  after: CurrentUser,
  roleId: string | null,
) {
  if (
    before.id !== after.id ||
    before.companyId !== after.companyId ||
    (roleId !== null && after.roleViewEnabled !== true) ||
    (after.roleView?.roleId ?? null) !== roleId
  )
    throw new Error('角色验收身份核验失败，原登录身份和草稿已保留。');
}
